import { useState, useEffect, useCallback } from "react";
import { Navigate } from "react-router-dom";
import { Loader2, Trash2, Star, Inbox, Users } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";

export default function Admin() {
  const { user, loading } = useAuth();
  const [attorneys, setAttorneys] = useState([]);
  const [leads, setLeads] = useState([]);
  const [busy, setBusy] = useState(true);

  const load = useCallback(async () => {
    setBusy(true);
    try {
      const [p, l] = await Promise.all([api.get("/attorneys"), api.get("/leads")]);
      setAttorneys(p.data);
      setLeads(l.data);
    } catch { toast.error("Could not load admin data"); }
    finally { setBusy(false); }
  }, []);

  useEffect(() => { if (user?.role === "admin") load(); }, [user, load]);

  if (loading || user === null) return <div className="min-h-[60vh] grid place-items-center"><Loader2 className="h-8 w-8 animate-spin text-forest-700" /></div>;
  if (!user || user.role !== "admin") return <Navigate to="/dashboard" replace />;

  const remove = async (id) => {
    try { await api.delete(`/attorneys/${id}`); setAttorneys((p) => p.filter((a) => a.attorney_id !== id)); toast.success("Attorney removed"); }
    catch { toast.error("Could not remove"); }
  };

  return (
    <div className="grain-bg min-h-screen">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <span className="font-mono text-xs uppercase tracking-[0.18em] text-terracotta">Admin console</span>
        <h1 className="mt-2 font-serif text-4xl sm:text-5xl font-semibold text-forest-900">Manage the marketplace</h1>
        <p className="mt-2 text-ink-700">Attorneys go live instantly on signup. Review the directory, remove bad-faith listings, and read incoming client leads.</p>

        <Tabs defaultValue="attorneys" className="mt-8">
          <TabsList className="bg-parchment-100 border border-parchment-200 rounded-full p-1 h-auto">
            <TabsTrigger value="attorneys" data-testid="admin-tab-attorneys" className="rounded-full data-[state=active]:bg-forest-800 data-[state=active]:text-parchment-50 px-5 py-2 gap-2"><Users className="h-4 w-4" /> Attorneys ({attorneys.length})</TabsTrigger>
            <TabsTrigger value="leads" data-testid="admin-tab-leads" className="rounded-full data-[state=active]:bg-forest-800 data-[state=active]:text-parchment-50 px-5 py-2 gap-2"><Inbox className="h-4 w-4" /> Leads ({leads.length})</TabsTrigger>
          </TabsList>

          <TabsContent value="attorneys" className="mt-6">
            {busy ? <Loader2 className="h-6 w-6 animate-spin text-forest-700" /> : attorneys.length === 0 ? (
              <p className="text-ink-500">No attorneys listed yet.</p>
            ) : (
              <div className="space-y-3">
                {attorneys.map((a) => (
                  <div key={a.attorney_id} data-testid={`attorney-row-${a.attorney_id}`} className="rounded-2xl border border-parchment-200 bg-white p-5 flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                    <div>
                      <div className="flex items-center gap-2">
                        <h3 className="font-semibold text-forest-900">{a.name}</h3>
                        {a.featured && <span className="inline-flex items-center gap-1 text-terracotta text-xs"><Star className="h-3 w-3" /> Featured</span>}
                      </div>
                      <p className="text-sm text-ink-500">{a.firm} · {(a.states || []).join(", ")}</p>
                      {a.bio && <p className="text-sm text-ink-600 mt-1 max-w-2xl line-clamp-2">{a.bio}</p>}
                    </div>
                    <Button onClick={() => remove(a.attorney_id)} data-testid={`remove-${a.attorney_id}`} variant="ghost" size="sm" className="text-ink-400 hover:text-destructive gap-1.5 shrink-0"><Trash2 className="h-4 w-4" /> Remove</Button>
                  </div>
                ))}
              </div>
            )}
          </TabsContent>

          <TabsContent value="leads" className="mt-6">
            {busy ? <Loader2 className="h-6 w-6 animate-spin text-forest-700" /> : leads.length === 0 ? (
              <p className="text-ink-500">No leads yet.</p>
            ) : (
              <div className="space-y-3">
                {leads.map((l) => (
                  <div key={l.lead_id} data-testid={`lead-${l.lead_id}`} className="rounded-2xl border border-parchment-200 bg-white p-5">
                    <div className="flex items-center justify-between">
                      <h3 className="font-semibold text-forest-900">{l.name} → {l.attorney_name}</h3>
                      <span className="text-xs text-ink-400">{new Date(l.created_at).toLocaleString()}</span>
                    </div>
                    <p className="text-sm text-ink-500 mt-0.5">{l.email}{l.phone ? ` · ${l.phone}` : ""}{l.state ? ` · ${l.state}` : ""}{l.charge ? ` · ${l.charge}` : ""}</p>
                    {l.message && <p className="text-sm text-ink-700 mt-2">{l.message}</p>}
                  </div>
                ))}
              </div>
            )}
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}
